import type { ReactNode } from 'react'

interface SegmentedItem<T extends string> {
  key: T
  // ReactNode (not string) so items can be plain labels (tabs/dims) OR JSX (Split/Full icon+text).
  label: ReactNode
  /** Optional leading dot color (e.g. the Issues-tab red dot). Omit for no dot. */
  dot?: string
}

interface SegmentedControlProps<T extends string> {
  items: SegmentedItem<T>[]
  value: T
  onChange: (key: T) => void
  /** Pill density. `xs` = dense footer controls; `sm` = compact toolbar (S1 default); `md` = roomier. */
  size?: 'xs' | 'sm' | 'md'
  /** Track surface. `a` = bg-gis-track (tabs / Split-Full); `b` = bg-gis-track-2 (breakdown dims). */
  track?: 'a' | 'b'
  /** Pill geometry. `pill` = fully rounded (tabs); `rounded` = 9px corners (Split/Full, dims). */
  shape?: 'pill' | 'rounded'
  /** Render for a navy header: translucent track, white active pill, white-muted inactive labels. */
  onDark?: boolean
  /** Accessible label for the tablist (e.g. "Panel view", "Breakdown dimension"). */
  'aria-label'?: string
  /** Extra utilities for margin / width — composed after the track classes (e.g. S4 dims `mb-[15px] w-full`). */
  className?: string
}

const SIZE: Record<'xs' | 'sm' | 'md', string> = {
  xs: 'px-[9px] py-[3px] text-[11.5px]',
  sm: 'px-[13px] py-[6px] text-[12.5px]',
  md: 'px-4 py-2 text-[13px]',
}

const TRACK: Record<'a' | 'b', string> = {
  a: 'bg-gis-track',
  b: 'bg-gis-track-2',
}

const SHAPE: Record<'pill' | 'rounded', { box: string; pill: string }> = {
  pill: { box: 'rounded-xl', pill: 'rounded-full' },
  rounded: { box: 'rounded-[10px]', pill: 'rounded-[8px]' },
}

/**
 * Generic segmented control: a track holding mutually-exclusive pill buttons. The active item
 * gets a white pill + soft shadow; inactive items are muted and brighten on hover. Presentational
 * only — owns no state; the parent supplies `value` and reacts to `onChange`. Used for the panel
 * tabs, Split/Full toggle, and breakdown dimension switch (spec §5).
 */
export function SegmentedControl<T extends string>({
  items,
  value,
  onChange,
  size = 'sm',
  track = 'a',
  shape = 'pill',
  onDark = false,
  'aria-label': ariaLabel,
  className = '',
}: SegmentedControlProps<T>) {
  const s = SHAPE[shape]
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`inline-flex gap-0.5 p-0.5 ${onDark ? 'bg-white/15' : TRACK[track]} ${s.box} ${className}`}
    >
      {items.map((item) => {
        const on = item.key === value
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(item.key)}
            className={`inline-flex items-center gap-1.5 font-semibold whitespace-nowrap transition-colors focus-visible:ring-2 ${onDark ? 'focus-visible:ring-white/60' : 'focus-visible:ring-blue-500/60'} ${SIZE[size]} ${s.pill} ${
              on
                ? 'bg-white text-gis-ink shadow-[0_1px_2px_rgba(15,31,61,.08)]'
                : onDark
                  ? 'text-white/80 hover:text-white'
                  : 'text-gis-soft hover:text-gis-ink'
            }`}
          >
            {item.dot !== undefined && (
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: on ? item.dot : 'var(--gis-muted)' }}
              />
            )}
            {item.label}
          </button>
        )
      })}
    </div>
  )
}

export type { SegmentedItem, SegmentedControlProps }
